// Scholarship Eligibility Criteria for Match Calculation

import { MajorCategory, Ethnicity, ExtracurricularCategory, FinancialNeedLevel } from './profile';

 // Grade level types
 // Canonical grade level values — all eligibility data MUST use these formats.
 // Middle-school: 'middle-school-6th' through 'middle-school-8th'
 // High-school: 'high-school-freshman' through 'high-school-senior'
 // College: 'college-freshman' through 'college-senior'
 // Graduate: 'graduate'
 export type GradeLevel =
   | 'middle-school-6th' | 'middle-school-7th' | 'middle-school-8th'
   | 'high-school-freshman' | 'high-school-sophomore' | 'high-school-junior' | 'high-school-senior'
   | 'hs-graduate'
   | 'college-freshman' | 'college-sophomore' | 'college-junior' | 'college-senior'
   | 'graduate';
 
 // Citizenship status types
 export type CitizenshipStatus = 'us-citizen' | 'permanent-resident' | 'daca' | 'undocumented' | 'international';
 
export interface EligibilityCriteria {
   // Academic requirements
  minGPA?: number;
   maxGPA?: number;
  
   // Age requirements
   minAge?: number;
   maxAge?: number;
   
   // Grade level requirements
   targetGradeLevels?: GradeLevel[];
   
  // Major/field requirements
  requiredMajors?: string[];
  
  // Demographics
  targetEthnicities?: string[]; // Empty = open to all
  targetGenders?: ('male' | 'female' | 'non-binary' | 'Male' | 'Female' | 'Non-Binary' | 'non_binary')[];
  
   // Location/Residency
  targetStates?: string[]; // Empty = national
   residencyRequired?: boolean | string[];
  
  // Financial
  requiresFinancialNeed?: boolean;
  requiresPellGrant?: boolean;
  requiresFAFSA?: boolean;
  maxHouseholdIncome?: string | number;
  
  // Background
  requiresFirstGen?: boolean;
   citizenshipRequired?: CitizenshipStatus[] | string;
  
  // Extracurriculars
  requiredActivities?: string[];
  
   // Special requirements
   requiresDisability?: boolean;
   requiresMilitaryConnection?: boolean;
   requiresFosterCare?: boolean;
   requiresEmployment?: string | boolean;
   requiresMembership?: string | boolean;
   
   // Application type
   noEssayRequired?: boolean;
   videoRequired?: boolean;
   nominationRequired?: boolean;
   
   // Extended criteria
   requiresGender?: string;
   ethnicityRequired?: string[];
   minFollowers?: number;
   specialRequirements?: string;
}

export interface MatchResult {
  overallMatch: number; // 0-100
  criteriaResults: CriteriaMatch[];
  eligible: boolean;
  /** Criteria that exist in eligibility data but can't be verified against the user profile */
  uncheckableCriteria: UncheckableCriterion[];
  /** 'full' when all criteria were checked, 'partial' when some couldn't be verified */
  confidence: 'full' | 'partial';
}

export interface UncheckableCriterion {
  label: string;
  description: string;
}

export interface CriteriaMatch {
  criterion: string;
  label: string;
  matches: boolean;
  required: boolean; // If required and doesn't match, user is ineligible
  weight: number; // How much this affects the score
  userValue?: string;
  requiredValue?: string;
   description?: string;
}

 // Human-readable eligibility requirement for display
 export interface EligibilityRequirement {
   id: string;
   label: string;
   description: string;
   category: 'academic' | 'demographic' | 'financial' | 'location' | 'background' | 'other';
   isRequired: boolean;
   icon?: string;
 }
 
 // Grade level display labels
 export const gradeLevelLabels: Record<GradeLevel, string> = {
   'middle-school-6th': '6th Grade',
   'middle-school-7th': '7th Grade',
   'middle-school-8th': '8th Grade',
   'high-school-freshman': 'High School Freshman',
   'high-school-sophomore': 'High School Sophomore',
   'high-school-junior': 'High School Junior',
   'high-school-senior': 'High School Senior',
   'hs-graduate': 'High School Graduate',
   'college-freshman': 'College Freshman',
   'college-sophomore': 'College Sophomore',
   'college-junior': 'College Junior',
   'college-senior': 'College Senior',
   'graduate': 'Graduate Student',
 };
 
 // Citizenship display labels
 export const citizenshipLabels: Record<CitizenshipStatus, string> = {
   'us-citizen': 'U.S. Citizen',
   'permanent-resident': 'Permanent Resident',
   'daca': 'DACA Recipient',
   'undocumented': 'Undocumented',
   'international': 'International Student',
 };
